import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import type { ConversationFilter, ConversationSummary } from '@/lib/api';
import { ConversationRow } from '@/components/ConversationRow';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { FilterChips } from '@/components/FilterChips';
import { PullToRefreshIndicator } from '@/components/PullToRefreshIndicator';
import { SearchBar } from '@/components/SearchBar';
import { SkeletonRow } from '@/components/Skeleton';
import { useConversations } from '@/hooks/useConversations';
import { useCreateQuote } from '@/hooks/useCreateQuote';
import { NewQuoteModal, type NewQuoteFormValues } from '@/components/NewQuoteModal';
import { Toast } from '@/components/Toast';
import { DEFAULT_SERVICE_PRESETS } from '@/lib/servicePresets';
import { markConversationsReadBulkRemote } from '@/lib/api';
import {
  isConversationForceUnread,
  isConversationLocallyRead,
  isConversationPinned,
  markConversationReadAndClearUnread,
} from '@/lib/readState';
import {
  applyConversationFilter,
  computeConversationFilterCounts,
} from '@/lib/conversationFilters';

// 2026-06-02 (bundle A) — six new filter chips ship client-side over the
// loaded conversation list. The full set:
const VALID_FILTERS: readonly ConversationFilter[] = [
  'all',
  'needs',
  'quoted',
  'booked',
  'resolved',
  'called',
  'hot',
  'drip_active',
  'awaiting_reply',
  'quoted_no_booking',
  'owner_handled',
  'stale',
] as const;

// Item 1 (2026-06-05) — "Unread only" is a cross-cutting toggle layered on
// top of whichever chip filter is active, NOT a chip itself (it composes with
// every chip). Persisted so the owner's choice survives navigation + reloads.
const UNREAD_ONLY_KEY = 'ss_convo_unread_only_v1';

function readUnreadOnly(): boolean {
  try {
    return window.localStorage.getItem(UNREAD_ONLY_KEY) === '1';
  } catch {
    return false;
  }
}

function writeUnreadOnly(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(UNREAD_ONLY_KEY, '1');
    else window.localStorage.removeItem(UNREAD_ONLY_KEY);
  } catch {
    /* ignore — private mode etc. */
  }
}

function parseFilter(raw: string | null): ConversationFilter {
  if (raw && (VALID_FILTERS as readonly string[]).includes(raw)) {
    return raw as ConversationFilter;
  }
  // Default to "Needs you" — when the owner taps the Chats tab fresh, the
  // first thing they should see is the work that needs them. The badge on
  // the Chats tab counts new convos (unread); they can flip to "All" to see
  // everything. Ernie 2026-05-22.
  return 'needs';
}

export function Conversations() {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();

  const filter = parseFilter(searchParams.get('filter'));
  const search = searchParams.get('q') ?? '';

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!value) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleFilterChange = useCallback(
    (next: ConversationFilter) => {
      // 'needs' is the default — URL stays clean when that chip is selected.
      // Any other selection persists in the URL so navigating away + back
      // returns to that filter view.
      updateParam('filter', next === 'needs' ? null : next);
    },
    [updateParam],
  );

  const handleSearchChange = useCallback(
    (next: string) => {
      updateParam('q', next.trim() ? next : null);
    },
    [updateParam],
  );

  // 2026-06-02 (bundle A) — always fetch with filter='all' from the Worker
  // so the loaded set is uniform across chip clicks. Active filter is
  // applied CLIENT-SIDE via applyConversationFilter below. This makes the
  // chip-switch instant (no refetch) and lets every chip's count badge
  // be accurate against the same loaded set.
  const query = useConversations({ slug, filter: 'all', search });
  const createQuote = useCreateQuote(slug);
  const [newQuoteOpen, setNewQuoteOpen] = useState(false);
  const [newQuoteToast, setNewQuoteToast] = useState<
    { kind: 'success' | 'error'; text: string } | null
  >(null);

  // Auto-clear the new-quote toast after a few seconds.
  useEffect(() => {
    if (!newQuoteToast) return;
    const t = window.setTimeout(() => setNewQuoteToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [newQuoteToast]);

  const handleNewQuoteSubmit = useCallback(
    (values: NewQuoteFormValues) => {
      createQuote.mutate(
        {
          phone: values.phone,
          first_name: values.first_name,
          last_name: values.last_name,
          amount: values.amount,
          service_label: values.service_label,
          vehicle: values.vehicle,
          send_recap: values.send_recap,
        },
        {
          onSuccess: (result) => {
            setNewQuoteOpen(false);
            setNewQuoteToast({
              kind: 'success',
              text: result.recap_triggered
                ? `Quote saved + recap sent to ${result.first_name}`
                : `Quote saved for ${result.first_name}`,
            });
            // Refetch the list so the new contact's thread appears.
            void query.refetch();
          },
          onError: (err) =>
            setNewQuoteToast({
              kind: 'error',
              text: err instanceof Error ? `Failed: ${err.message}` : 'Failed to save quote',
            }),
        },
      );
    },
    [createQuote, query],
  );
  const {
    data,
    isLoading,
    isError,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  } = query;

  // pinVersion bumps whenever a row toggles pin / read / unread via swipe.
  // Used as an extra dependency on the sort memo below so pinned items
  // re-sort to the top instantly without a refetch.
  const [pinVersion, setPinVersion] = useState(0);
  const handleLocalStateChange = useCallback(() => {
    setPinVersion((v) => v + 1);
  }, []);

  // Loaded set from the server (always fetched with filter='all' since 2026-06-02).
  // Pinned-first sort applied here so the underlying order is stable across
  // chip clicks. Client-side filter is applied to THIS set, not the raw flat.
  const conversations: ConversationSummary[] = useMemo(() => {
    const flat = data?.pages.flatMap((p) => p.conversations) ?? [];
    // Stable partition: pinned first (in original order), unpinned after.
    // Reads pin state from localStorage during sort. pinVersion in deps
    // ensures we re-sort when a row's pin state flips.
    const pinned: ConversationSummary[] = [];
    const rest: ConversationSummary[] = [];
    for (const c of flat) {
      if (isConversationPinned(c.id)) pinned.push(c);
      else rest.push(c);
    }
    return [...pinned, ...rest];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pinVersion]);

  // 2026-06-02 (bundle A) — client-side filtered + sorted view.
  // applyConversationFilter handles per-chip predicates, including the
  // 'hot' chip's newest-first sort within the filter.
  const displayConversations: ConversationSummary[] = useMemo(
    () => applyConversationFilter(conversations, filter),
    [conversations, filter],
  );

  // 2026-06-02 (bundle A) — count badge for every chip, computed over the
  // SAME loaded set so all chips show accurate counts regardless of which
  // one is active. Recomputes whenever `conversations` changes (refetch /
  // pinVersion bump / new page loaded).
  const chipCounts = useMemo(
    () => computeConversationFilterCounts(conversations),
    [conversations],
  );

  // Item 1 (2026-06-05) — "Unread only" toggle. Layered on top of the active
  // chip filter. Unread truth is the SAME effective-unread logic the row blue
  // dots and "Mark all read" button use (api.unread OR local force-unread,
  // AND not locally read) — no second definition (per spec).
  const [unreadOnly, setUnreadOnly] = useState(readUnreadOnly);
  const toggleUnreadOnly = useCallback(() => {
    setUnreadOnly((on) => {
      const next = !on;
      writeUnreadOnly(next);
      return next;
    });
  }, []);

  // Effective-unread predicate, reused by the toggle count, the "Mark all
  // read" button, and the unread-only filter below. pinVersion is threaded
  // through the memos that consume this so localStorage read/unread writes
  // re-evaluate it without a remount.
  const isVisiblyUnread = useCallback(
    (c: ConversationSummary) =>
      (c.unread || isConversationForceUnread(c.id)) &&
      !isConversationLocallyRead(c.id, c.last_message.timestamp_iso),
    [],
  );

  // Final rendered list = chip-filtered set, optionally narrowed to unread.
  const visibleConversations: ConversationSummary[] = useMemo(
    () => (unreadOnly ? displayConversations.filter(isVisiblyUnread) : displayConversations),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayConversations, unreadOnly, isVisiblyUnread, pinVersion],
  );

  // Unread count within the CURRENT chip view (drives both the toggle badge
  // and the "Mark all read" button so they always agree).
  const unreadInView = useMemo(
    () => displayConversations.filter(isVisiblyUnread).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayConversations, isVisiblyUnread, pinVersion],
  );

  // --- Infinite scroll sentinel ----------------------------------------
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '120px 0px 120px 0px' },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // --- Pull-to-refresh (touch events via usePullToRefresh hook) --------
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const { pullDistance, refreshing, triggerPx } = usePullToRefresh({
    scrollContainerRef,
    onRefresh: () => refetch(),
  });

  const handleRowSelect = useCallback(
    (id: string) => {
      navigate(`/${slug}/conversations/${id}`);
    },
    [navigate, slug],
  );

  // --- Render branches -------------------------------------------------
  // Skeleton / error tied to the SERVER fetch (raw loaded set).
  // Empty state ties to the FILTERED view so "No conversations" shows
  // even when other chips have results.
  const showInitialSkeleton = isLoading && conversations.length === 0;
  const showError = isError && conversations.length === 0;
  const showEmpty = !isLoading && !isError && visibleConversations.length === 0;

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-ink-50 dark:bg-[#0B1220]">
      {/* Static header — search bar + filter chips (+ mark-all-read) stay
          pinned at the top; only the list below scrolls (Ernie 2026-05-21).
          SearchBar owns the safe-area-top inset so it clears the notch. */}
      <div className="shrink-0">
        <SearchBar value={search} onChange={handleSearchChange} />
        <FilterChips value={filter} onChange={handleFilterChange} counts={chipCounts} />

      {/* Header action row — "Unread" toggle (Item 1, 2026-06-05) + "Mark
          all read". Both read from the SAME effective-unread logic the row
          blue dots use (effectiveUnread = (api.unread || localForceUnread)
          && !localRead) so counts always agree + survive server refetches.
          The row shows whenever there's something unread in the current chip
          view OR the unread-only filter is engaged — so the owner can flip
          the filter back off from the "All caught up" empty state instead of
          being stranded. pinVersion threads through unreadInView so a
          localStorage read/unread write re-evaluates without a remount. */}
      {unreadInView > 0 || unreadOnly ? (
        <div className="flex items-center justify-between gap-2 px-4 pb-2 pt-1">
          <button
            type="button"
            onClick={toggleUnreadOnly}
            aria-pressed={unreadOnly}
            aria-label={unreadOnly ? 'Show all conversations' : 'Show unread only'}
            className={[
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold transition-colors',
              unreadOnly
                ? 'bg-ss-blue text-white'
                : 'bg-ink-100 text-ink-600 active:bg-ink-200 dark:bg-[#1E293B] dark:text-[#94A3B8]',
            ].join(' ')}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
            <span>Unread{unreadInView > 0 ? ` (${unreadInView})` : ''}</span>
          </button>
          {unreadInView > 0 ? (
            <button
              type="button"
              onClick={() => {
                const now = Date.now();
                const nowIso = new Date(now).toISOString();
                const idsToFlip = new Set<string>();
                for (const c of displayConversations) {
                  if (isVisiblyUnread(c)) {
                    markConversationReadAndClearUnread(c.id, nowIso);
                    idsToFlip.add(c.id);
                  }
                }
                // Persist the read marks server-side in one batch (durable +
                // cross-device). Best-effort — local state already updated.
                void markConversationsReadBulkRemote(
                  slug,
                  Array.from(idsToFlip),
                  now,
                ).catch(() => {});
                // Best-effort cache patch so any other surface reading
                // c.unread (e.g. tab badges) also flips in the same
                // frame. Even if the next refetch reverts this, the
                // button + row blue dots still stay correct because they
                // source from isConversationLocallyRead() instead.
                qc.setQueriesData<{
                  pages?: Array<{ conversations?: ConversationSummary[] }>;
                }>({ queryKey: ['conversations', slug] }, (old) => {
                  if (!old?.pages) return old;
                  let mutated = false;
                  const pages = old.pages.map((page) => {
                    if (!page?.conversations) return page;
                    const items = page.conversations.map((c) => {
                      if (idsToFlip.has(c.id) && c.unread) {
                        mutated = true;
                        return { ...c, unread: false };
                      }
                      return c;
                    });
                    return mutated ? { ...page, conversations: items } : page;
                  });
                  return mutated ? { ...old, pages } : old;
                });
                handleLocalStateChange();
              }}
              className="rounded-full bg-ss-blue/10 px-3 py-1 text-[12px] font-semibold text-ss-blue active:bg-ss-blue/20"
            >
              Mark all read ({unreadInView})
            </button>
          ) : null}
        </div>
      ) : null}
      </div>

      {/* Scrollable list — owns the only scroll context on this screen. */}
      <div
        ref={scrollContainerRef}
        className="relative min-h-0 flex-1 overflow-y-auto overscroll-y-contain"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <PullToRefreshIndicator
          pullDistance={pullDistance}
          refreshing={refreshing}
          triggerPx={triggerPx}
        />

      <div className="bg-white dark:bg-[#0F172A]">
        {showInitialSkeleton ? (
          <SkeletonRow count={6} />
        ) : showError ? (
          <ErrorState
            title="Couldn’t load conversations"
            message={error instanceof Error ? error.message : 'Please try again.'}
            onRetry={() => void refetch()}
          />
        ) : showEmpty ? (
          <EmptyState
            icon={
              !search && unreadOnly ? (
                // "All caught up" — check-circle reads as done, not broken.
                <svg
                  width={36}
                  height={36}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              ) : (
                <svg
                  width={36}
                  height={36}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              )
            }
            title={search ? 'No matches' : unreadOnly ? 'All caught up' : 'No conversations yet'}
            body={
              search
                ? 'Try a different name or phone number.'
                : unreadOnly
                  ? 'No unread conversations right now. Tap Unread to see everything.'
                  : 'When Shelby talks to a caller, the thread shows up here.'
            }
          />
        ) : (
          <>
            {visibleConversations.map((c, i) => (
              <div
                key={c.id}
                className="animate-ss-fade-in"
                // Stagger the entrance — each row 28ms after the previous
                // up to a cap so a long list still feels snappy. Same trick
                // Insta uses on feed mount.
                style={{ animationDelay: `${Math.min(i, 12) * 28}ms` }}
              >
                <ConversationRow
                  conversation={c}
                  onSelect={handleRowSelect}
                  slug={slug}
                  onLocalStateChange={handleLocalStateChange}
                  stateVersion={pinVersion}
                />
              </div>
            ))}
            {isFetchingNextPage ? <SkeletonRow count={2} /> : null}
            {hasNextPage ? <div ref={sentinelRef} className="h-px" aria-hidden="true" /> : null}
            {!hasNextPage && visibleConversations.length > 0 ? (
              <div className="px-4 py-6 text-center text-xs text-ink-400">
                {unreadOnly ? 'That’s every unread.' : 'That’s everything.'}
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Top-of-list refresh indicator (non-touch refetches) */}
      {isFetching && !isLoading && !isFetchingNextPage && !refreshing ? (
        <div className="pointer-events-none absolute right-3 top-2 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold text-ink-500 shadow-ss-sm dark:bg-[#1E293B]/90 dark:text-[#94A3B8]">
          Updating…
        </div>
      ) : null}
      </div>

      {/* New Quote FAB — sits above the tab bar (--tab-h=64px), to the
          right. Used for cold owner-handled calls where the customer
          doesn't yet exist in the Conversations list. Tapping opens the
          NewQuoteModal which collects phone + name + quote details and
          fires the same recap/drip path Shelby's analyzer uses. */}
      <button
        type="button"
        onClick={() => setNewQuoteOpen(true)}
        aria-label="Log a new quote you took on the phone"
        className="fixed right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-ss-blue text-white shadow-ss-md transition-all duration-150 ease-ss-standard hover:bg-ss-blue-dark active:scale-95"
        style={{
          // Sit ~16px above the floating tab dock. The dock is flush to the
          // bottom edge and intentionally overlaps the home indicator (it
          // reserves NO safe-area space — see TabBar.tsx), so we must NOT add
          // env(safe-area-inset-bottom) here or the FAB floats a full row too
          // high. --tab-h already covers the dock height.
          bottom: 'calc(var(--tab-h, 64px) + 16px)',
        }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-6 w-6"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>

      <NewQuoteModal
        open={newQuoteOpen}
        busy={createQuote.isPending}
        servicePresets={[...DEFAULT_SERVICE_PRESETS]}
        onCancel={() => {
          if (createQuote.isPending) return;
          setNewQuoteOpen(false);
        }}
        onConfirm={handleNewQuoteSubmit}
      />

      {/* New-quote success/error toast — shared pill, above the tab bar +
          FAB so it doesn't collide. */}
      {newQuoteToast && (
        <Toast
          kind={newQuoteToast.kind}
          text={newQuoteToast.text}
          bottomOffset="calc(var(--tab-h, 64px) + 96px)"
        />
      )}
    </div>
  );
}
